import { useTranslation } from 'react-i18next';
import { Difficulty, DIFFICULTIES, Language, Task, TaskStatus, TaskType } from '@quizdb/common';
import { Icon } from '../common/Icon';

/** A task's status as a tinted pill with a dot (QZ-D37, QZ-D47). */
export const TaskStatusPill = ({ status }: { status: TaskStatus }) => {
  const { t } = useTranslation();
  const modifier = status === 'draft' ? '' : ` is-${status}`;
  return <span className={`qz-status${modifier}`}>{t(`task.status.${status}`)}</span>;
};

/** One task type the task can be served as (QZ-D16). */
export const TaskTypeBadge = ({ type }: { type: TaskType }) => {
  const { t } = useTranslation();
  return <span className="qz-type">{t(`task.type.${type}`)}</span>;
};

/** A task's tags as chips (QZ-D15). */
export const TagChips = ({ tags }: { tags: readonly string[] }) => (
  <div className="qz-tags">
    {tags.map((tag) => (
      <span key={tag} className="qz-tag">
        {tag}
      </span>
    ))}
  </div>
);

/** Difficulty as three bars filled up to its level, with its name (QZ-D14). */
export const DifficultyMeter = ({ difficulty }: { difficulty: Difficulty }) => {
  const { t } = useTranslation();
  const level = DIFFICULTIES.indexOf(difficulty);
  return (
    <span className="qz-diff">
      <span className="qz-diff-bars" aria-hidden="true">
        {DIFFICULTIES.map((step, index) => (
          <i key={step} className={index <= level ? 'is-on' : undefined} />
        ))}
      </span>
      {t(`task.difficulty.${difficulty}`)}
    </span>
  );
};

/** A language as its code, with its name on hover (QZ-D12). */
export const LanguageCode = ({ language }: { language: Language }) => {
  const { t } = useTranslation();
  return (
    <span className="qz-lang" title={t(`task.language.${language}`)}>
      {language}
    </span>
  );
};

/**
 * Where a task's fact came from, in one short line: the source's host, or its document and page
 * (QZ-D14). Nothing for an open-web task, which cites no source.
 */
export const SourceHint = ({ source }: { source: Task['sourceReference'] }) => {
  const { t } = useTranslation();
  if (!source) {
    return null;
  }
  if (source.url) {
    let host = source.url;
    try {
      host = new URL(source.url).host;
    } catch {
      // A stored URL is valid (QZ-D42); fall back to the raw string all the same.
    }
    return (
      <span className="qz-small qz-muted">
        <Icon name="link" /> {host}
      </span>
    );
  }
  if (source.document) {
    const label = source.kind === 'photos' ? 'task.field.photoShort' : 'task.field.pageShort';
    return (
      <span className="qz-small qz-muted">
        <Icon name={source.kind === 'photos' ? 'images' : 'file-pdf'} /> {source.document}
        {source.page !== undefined && ` · ${t(label, { page: source.page })}`}
      </span>
    );
  }
  return null;
};

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/** `iso` relative to `now` ("2 hours ago", "yesterday"), in `locale`. */
export const formatRelative = (iso: string, locale: string, now = Date.now()): string => {
  const seconds = (new Date(iso).getTime() - now) / 1000;
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) {
      return format.format(Math.round(seconds / size), unit);
    }
  }
  return format.format(0, 'minute');
};

/** A timestamp shown relative to now, with the full time on hover (QZ-D47). */
export const RelativeTime = ({ iso }: { iso: string }) => {
  const { i18n } = useTranslation();
  return (
    <time dateTime={iso} title={new Date(iso).toLocaleString(i18n.language)}>
      {formatRelative(iso, i18n.language)}
    </time>
  );
};
